import { DeviceType } from '@/types/api';
import type { Device, ProvisionedMeter, Unit } from '@/types/api';
import {
  lookupDeviceBySerial,
  createDevice,
  updateDevice,
  updateDeviceConfig,
  checkAwsIotStatus,
  provisionAwsIot,
  DeviceMultiplier,
  DeviceUnitOfMeasure,
} from '@/features/devices/api/devices-api';
import { lookupProvisionedMeter } from '@/features/device-database/api/provisioned-meters-api';
import { getUnits, createUnit } from '@/features/units/api/units-api';
import { detectDeviceType } from './detect-device-type';

export type SetupDeviceErrorCode =
  | 'serial-not-found'
  | 'missing-credentials'
  | 'aws-provision-failed'
  | 'unit-lookup-failed'
  | 'device-assign-failed'
  | 'state-changed';

export class SetupDeviceError extends Error {
  readonly code: SetupDeviceErrorCode;
  constructor(code: SetupDeviceErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = 'SetupDeviceError';
  }
}

export interface ScanLookupInput {
  buildingId: string;
  serialNumber: string;
  unitNumber: string;
}

export interface SetupCommitInput {
  lookup: ScanLookupResult;
  buildingId: string;
  unitNumber: string;
  meterType?: DeviceType;
  // SALT-only pulse encoder config. Applied via updateDeviceConfig after
  // the device exists. Ignored for non-SALT meter types.
  saltMultiplier?: DeviceMultiplier;
  saltUnitOfMeasure?: DeviceUnitOfMeasure;
}

export interface SetupDeviceResult {
  device: Device;
  unit: Unit;
  // true when an existing device row was reassigned to a different unit,
  // false when a new Device was created or it was already on the target unit.
  reassigned: boolean;
}

// Discriminated result of the read-only lookup phase. The caller inspects
// `kind` to decide whether to render the reassignment confirm modal before
// committing.
//
//   - 'new'             : serial isn't in our org yet; commit creates a Device.
//   - 'same-unit'       : serial already on the target unit; commit is a no-op.
//   - 'silent-reassign' : serial exists but is currently unassigned; commit
//                         assigns to target unit without prompting.
//   - 'needs-confirm'   : serial is currently on a *different* unit; commit
//                         must wait on the user's confirm.
//
// `inferredDeviceType` is a best-effort classification from DevEUI bit 26 +
// JoinEUI signals (see `detect-device-type.ts`). Used to (a) drive the
// initial DeviceType on a 'new' commit and (b) surface "(SALT)" in the
// success banner immediately, before any uplink has been received.
export type ScanLookupResult =
  | {
      kind: 'new';
      serial: string;
      devEui: string;
      provisionedMeter: ProvisionedMeter | null;
      inferredDeviceType: DeviceType;
    }
  | { kind: 'same-unit'; serial: string; device: Device }
  | {
      kind: 'silent-reassign';
      serial: string;
      device: Device;
      devEui: string;
      provisionedMeter: ProvisionedMeter | null;
      inferredDeviceType: DeviceType;
    }
  | {
      kind: 'needs-confirm';
      serial: string;
      device: Device;
      devEui: string;
      provisionedMeter: ProvisionedMeter | null;
      inferredDeviceType: DeviceType;
      sourceBuildingName: string | null;
      sourceUnitNumber: string | null;
    };

function normalizeUnit(value: string): string {
  return value.trim().toUpperCase();
}

// Read-only: looks up the serial against the org and the global catalog,
// then classifies the scan into one of the four kinds above. Does not call
// AWS provisioning, does not create units. Safe to run twice (idempotent),
// which the caller takes advantage of right before commit to detect any
// concurrent changes between the modal opening and the user confirming.
export async function lookupDeviceForScan(input: ScanLookupInput): Promise<ScanLookupResult> {
  const serial = input.serialNumber.trim().toUpperCase();
  const targetUnit = normalizeUnit(input.unitNumber);

  const [deviceRes, provisionedRes] = await Promise.allSettled([
    lookupDeviceBySerial(serial),
    lookupProvisionedMeter(serial),
  ]);
  const foundDevice =
    deviceRes.status === 'fulfilled' && deviceRes.value.success && deviceRes.value.data
      ? deviceRes.value.data
      : null;
  const provisionedMeter =
    provisionedRes.status === 'fulfilled' && provisionedRes.value.success && provisionedRes.value.data
      ? provisionedRes.value.data
      : null;

  if (!foundDevice && !provisionedMeter) {
    throw new SetupDeviceError(
      'serial-not-found',
      `Meter ${serial} was not found. Verify the serial or add it to the device database first.`
    );
  }

  const devEui = foundDevice?.devEui ?? provisionedMeter?.devEui ?? '';
  if (!devEui) {
    throw new SetupDeviceError('serial-not-found', `Meter ${serial} has no DevEUI on record.`);
  }
  const inferredDeviceType = detectDeviceType(devEui, provisionedMeter?.joinEui);

  if (!foundDevice) {
    return { kind: 'new', serial, devEui, provisionedMeter, inferredDeviceType };
  }

  if (!foundDevice.unitId) {
    return {
      kind: 'silent-reassign',
      serial,
      device: foundDevice,
      devEui,
      provisionedMeter,
      inferredDeviceType,
    };
  }

  const sameTarget =
    foundDevice.buildingId === input.buildingId &&
    normalizeUnit(foundDevice.unitNumber ?? '') === targetUnit;
  if (sameTarget) {
    return { kind: 'same-unit', serial, device: foundDevice };
  }

  return {
    kind: 'needs-confirm',
    serial,
    device: foundDevice,
    devEui,
    provisionedMeter,
    inferredDeviceType,
    sourceBuildingName: foundDevice.buildingName ?? null,
    sourceUnitNumber: foundDevice.unitNumber ?? null,
  };
}

// Performs the writes implied by a lookup result: optional AWS IoT
// provisioning, unit upsert, device assign-or-create. Same-unit lookups
// short-circuit without touching the API.
export async function commitDeviceSetup(input: SetupCommitInput): Promise<SetupDeviceResult> {
  const { lookup, buildingId, unitNumber } = input;
  const unitNumberTrimmed = unitNumber.trim();

  if (lookup.kind === 'same-unit') {
    // Already on the target unit — return the device as-is. The unit object
    // isn't strictly needed by callers in this case, but we synthesize a
    // minimal one from the device's known fields to keep the return shape.
    return {
      device: lookup.device,
      unit: {
        id: lookup.device.unitId!,
        unitNumber: lookup.device.unitNumber ?? unitNumberTrimmed,
        buildingId,
      } as Unit,
      reassigned: false,
    };
  }

  // 1. AWS IoT provisioning — for new devices and for previously-unassigned
  //    devices that may have been re-flashed. Skipped when a check returns
  //    isProvisioned=true.
  const devEui = lookup.devEui;
  const provisionedMeter = lookup.provisionedMeter;

  try {
    const awsStatus = await checkAwsIotStatus(devEui);
    if (!awsStatus.isProvisioned) {
      if (!provisionedMeter) {
        throw new SetupDeviceError(
          'missing-credentials',
          `Meter ${lookup.serial} is not registered in AWS IoT and no credentials are available. Use the standalone setup flow to enter Join EUI and App Key.`
        );
      }
      await provisionAwsIot({
        serialNumber: lookup.serial,
        devEui: provisionedMeter.devEui,
        joinEui: provisionedMeter.joinEui,
        appKey: provisionedMeter.appKey,
      });
    }
  } catch (err) {
    if (err instanceof SetupDeviceError) throw err;
    throw new SetupDeviceError(
      'aws-provision-failed',
      `Could not register ${lookup.serial} in AWS IoT. ${extractMessage(err) ?? 'Try again in a moment.'}`
    );
  }

  // 2. Unit upsert — find by (buildingId, unitNumber) or create.
  let unit: Unit;
  try {
    const existing = await getUnits({ buildingId, search: unitNumberTrimmed, perPage: 50 });
    const match = existing.items.find((u) => u.unitNumber === unitNumberTrimmed);
    if (match) {
      unit = match;
    } else {
      const created = await createUnit({ buildingId, unitNumber: unitNumberTrimmed });
      if (!created.success || !created.data) {
        throw new SetupDeviceError('unit-lookup-failed', created.message ?? `Could not create unit ${unitNumberTrimmed}.`);
      }
      unit = created.data;
    }
  } catch (err) {
    if (err instanceof SetupDeviceError) throw err;
    throw new SetupDeviceError(
      'unit-lookup-failed',
      extractMessage(err) ?? `Could not look up or create unit ${unitNumberTrimmed}.`
    );
  }

  // 3. Device assignment.
  let result: SetupDeviceResult;
  try {
    if (lookup.kind === 'new') {
      // Prefer the explicit meterType the caller passed (e.g. installer
      // override), fall back to the EUI-based inference, then WaterMeter as
      // the conservative default. The backend's FPort-based detection on
      // first uplink remains the source of truth and will overwrite if this
      // initial guess turns out wrong.
      const created = await createDevice({
        unitId: unit.id,
        devEui,
        serialNumber: lookup.serial,
        deviceType: input.meterType ?? lookup.inferredDeviceType ?? DeviceType.WaterMeter,
      });
      if (!created.success || !created.data) {
        throw new SetupDeviceError('device-assign-failed', created.message ?? 'Could not create device.');
      }
      result = { device: created.data, unit, reassigned: false };
    } else {
      // silent-reassign or needs-confirm — both update an existing device.
      const found = lookup.device;
      const res = await updateDevice(found.id, { unitId: unit.id });
      if (!res.success) {
        throw new SetupDeviceError('device-assign-failed', res.message ?? 'Could not assign device to unit.');
      }
      const reassigned = lookup.kind === 'needs-confirm';
      result = {
        device: { ...found, unitId: unit.id, unitNumber: unit.unitNumber, buildingId },
        unit,
        reassigned,
      };
    }
  } catch (err) {
    if (err instanceof SetupDeviceError) throw err;
    throw new SetupDeviceError(
      'device-assign-failed',
      extractMessage(err) ?? 'Could not assign device to unit.'
    );
  }

  // 4. SALT pulse-encoder config — only when the installer flagged this
  //    batch as SALT and provided values. Failure here is non-fatal: the
  //    device is already in the target unit, the installer can correct
  //    the pulse config from the Edit Device modal afterwards.
  if (
    input.meterType === DeviceType.SALTTransmitter &&
    input.saltMultiplier != null &&
    input.saltUnitOfMeasure != null
  ) {
    try {
      await updateDeviceConfig(result.device.id, {
        multiplier: input.saltMultiplier,
        unitOfMeasure: input.saltUnitOfMeasure,
      });
    } catch (err) {
      console.warn('[setup] SALT config not applied; install continues:', err);
    }
  }

  return result;
}

function extractMessage(err: unknown): string | null {
  if (!err) return null;
  const response = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
  if (response) return response;
  if (err instanceof Error) return err.message;
  return null;
}
